import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  CircularProgress,
  Breadcrumbs,
  Link,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Chip,
  Paper,
  Alert,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../store/store';
import {
  createProduct,
  updateProduct,
  fetchProductById,
  clearError,
} from '../../store/slices/adminSlice';
import { fetchAdminCategories } from '../../store/slices/productSlice';
import { CreateProductRequest, UpdateProductRequest, Product, ProductImage } from '../../types';
import { toast } from 'react-toastify';
import { useSettings } from '../../contexts/SettingsContext';
import { getImageUrl } from '../../utils/imageUtils';
import api from '../../services/api';
import { convertBgnToEurDynamic, convertEurToBgnDynamic } from '../../utils/currency';

const validationSchema = Yup.object({
  name: Yup.string()
    .min(2, 'Name must be at least 2 characters')
    .max(255, 'Name must not exceed 255 characters')
    .required('Name is required'),
  description: Yup.string()
    .min(10, 'Description must be at least 10 characters')
    .max(2000, 'Description must not exceed 2000 characters')
    .required('Description is required'),
  price: Yup.number()
    .min(0.01, 'Price must be greater than 0')
    .max(99999.99, 'Price must not exceed $99,999.99')
    .required('Price is required'),
  discountPrice: Yup.number()
    .min(0, 'Discount price cannot be negative')
    .max(99999.99, 'Discount price must not exceed $99,999.99')
    .test('discount-less-than-price', 'Discount price must be less than regular price', function(value) {
      if (value === undefined || value === null) return true;
      const { price } = this.parent;
      return !price || value < price;
    }),
  stockQuantity: Yup.number()
    .min(0, 'Stock quantity cannot be negative')
    .integer('Stock quantity must be a whole number')
    .required('Stock quantity is required'),
  categoryIds: Yup.array()
    .of(Yup.number())
    .min(1, 'At least one category is required')
    .required('Categories are required'),
  isbn: Yup.string()
    .nullable()
    .test('isbn-format', 'ISBN номер трябва да бъде валиден 10-цифрен (напр. 1-57181-655-0) или 13-цифрен (напр. 978-619-XXXX-25-0) формат', function(value) {
      if (!value || value.trim() === '') return true; // Allow empty ISBN
      const cleanValue = value.replace(/[-\s]/g, ''); // Remove hyphens and spaces
      // 10-digit ISBN: 9 digits + 1 check digit (can be X)
      // 13-digit ISBN: 13 digits (starts with 978 or 979)
      if (cleanValue.length === 10) {
        return /^\d{9}[\dX]$/.test(cleanValue);
      } else if (cleanValue.length === 13) {
        return /^(978|979)\d{10}$/.test(cleanValue);
      }
      return false;
    }),
  author: Yup.string()
    .max(255, 'Author name must not exceed 255 characters'),
  publisher: Yup.string()
    .max(255, 'Publisher name must not exceed 255 characters'),
  publicationDate: Yup.date()
    .max(new Date(), 'Publication date cannot be in the future'),
  pages: Yup.number()
    .min(1, 'Pages must be at least 1')
    .integer('Pages must be a whole number'),
  language: Yup.string()
    .max(50, 'Language must not exceed 50 characters'),
});

const ProductEdit: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const { loading, error, products } = useSelector((state: RootState) => state.admin);
  const { categories } = useSelector((state: RootState) => state.products);
  const { currencySettings } = useSettings();
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get current product from Redux store
  const currentProduct = id && id !== 'new' ? products.find(p => p.id === parseInt(id)) : null;

  useEffect(() => {
    dispatch(fetchAdminCategories() as any);
    
    if (id && id !== 'new') {
      setIsEditMode(true);
      const productId = parseInt(id);
      const product = products.find(p => p.id === productId);
      if (product) {
        setProductImages(product.images || []);
      } else {
        // Product not in store, fetch it by ID
        dispatch(fetchProductById(productId) as any);
      }
    }
  }, [dispatch, id, products]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Update product images when current product changes
  useEffect(() => {
    if (currentProduct) {
      setProductImages(currentProduct.images || []);
    }
  }, [currentProduct]);

  const formik = useFormik({
    initialValues: {
      name: currentProduct?.title || '',
      description: currentProduct?.description || '',
      price: currentProduct?.price || 0,
      discountPrice: currentProduct?.discountPrice || undefined,
      stockQuantity: currentProduct?.stock || 0,
      categoryIds: currentProduct?.categories?.map(cat => cat.id) || [],
      isbn: currentProduct?.isbn || '',
      author: currentProduct?.author || '',
      publisher: currentProduct?.publisher || '',
      publicationDate: currentProduct?.publicationYear ? 
        currentProduct.publicationYear.toString() + '-01-01' : 
        (currentProduct?.publicationDate ? 
          new Date(currentProduct.publicationDate).toISOString().split('T')[0] : ''),
      pages: currentProduct?.pages || 0,
      language: currentProduct?.language || '',
      isActive: currentProduct?.active ?? true,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (isEditMode && currentProduct) {
          const updateData: UpdateProductRequest = {
            id: currentProduct.id,
            ...values,
            title: values.name,
            stock: values.stockQuantity,
            active: values.isActive,
            publicationDate: values.publicationDate || undefined,
          };
          await dispatch(updateProduct({ productId: currentProduct.id, data: updateData }) as any);
          toast.success('Product updated successfully');
        } else {
          const createData: CreateProductRequest = {
            title: values.name,
            description: values.description,
            price: values.price,
            discountPrice: values.discountPrice,
            categoryIds: values.categoryIds,
            stock: values.stockQuantity, // Map stockQuantity to stock
            isbn: values.isbn,
            author: values.author,
            publisher: values.publisher,
            publicationDate: values.publicationDate || undefined,
            pages: values.pages,
            language: values.language,
            active: values.isActive,
          };
          await dispatch(createProduct(createData) as any);
          toast.success('Product created successfully');
        }
        navigate('/admin/products');
      } catch (error) {
        toast.error(isEditMode ? 'Failed to update product' : 'Failed to create product');
      }
    },
  });

  const handleGoBack = () => {
    navigate('/admin/products');
  };

  // Image upload functionality
  const handleImageUpload = useCallback(async (files: File[]) => {
    if (!isEditMode || !currentProduct) {
      toast.error('Please save the product first before uploading images');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    
    files.forEach((file, index) => {
      formData.append('files[]', file);
    });

    try {
      const response = await api.post(`/admin/products/${currentProduct.id}/images`, formData, {
        headers: {
          'Content-Type': undefined, // Let axios set multipart/form-data with boundary
        },
      });

      if (response.data.success) {
        setProductImages(prev => [...prev, ...response.data.images]);
        toast.success('Images uploaded successfully');
      } else {
        toast.error(response.data.message || 'Failed to upload images');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      if (error.response?.data) {
        console.error('Error details:', error.response.data);
        toast.error(error.response.data.message || 'Failed to upload images');
      } else {
        toast.error('Failed to upload images');
      }
    } finally {
      setUploading(false);
    }
  }, [isEditMode, currentProduct]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      handleImageUpload(files);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      handleImageUpload(files);
    }
  }, [handleImageUpload]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragActive(false);
  }, []);

  const handleDeleteImage = async (imageId: number) => {
    try {
      const response = await api.delete(`/admin/products/images/${imageId}`);

      setProductImages(prev => prev.filter(img => img.id !== imageId));
      toast.success('Image deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleSetPrimaryImage = async (imageId: number) => {
    if (!currentProduct) return;

    try {
      const response = await api.put(`/admin/products/${currentProduct.id}/images/${imageId}/primary`);

      setProductImages(prev => prev.map(img => ({
        ...img,
        isPrimary: img.id === imageId
      })));
      toast.success('Primary image set successfully');
    } catch (error) {
      console.error('Set primary error:', error);
      toast.error('Failed to set primary image');
    }
  };

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/admin/products"
          onClick={(e) => {
            e.preventDefault();
            navigate('/admin/products');
          }}
        >
          Products
        </Link>
        <Typography color="text.primary">
          {isEditMode ? 'Edit Product' : 'Create Product'}
        </Typography>
      </Breadcrumbs>

      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
        >
          Back to Products
        </Button>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {isEditMode ? 'Edit Product' : 'Create New Product'}
        </Typography>
      </Box>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Product Name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      name="description"
                      label="Description"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.description && Boolean(formik.errors.description)}
                      helperText={formik.touched.description && formik.errors.description}
                      required
                    />
                  </Grid>
{/* Adaptive Currency Price Fields */}
                  {currencySettings.currency_display_mode === 'dual' ? (
                    // Dual Currency Mode - BGN input with EUR display
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          name="price"
                          label="Price (BGN)"
                          value={formik.values.price}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.price && Boolean(formik.errors.price)}
                          helperText={formik.touched.price && formik.errors.price}
                          required
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          name="priceEUR"
                          label="Price (EUR)"
                          value={formik.values.price ? 
                            convertBgnToEurDynamic(formik.values.price, currencySettings.bgn_to_eur_rate).toFixed(2) : ''
                          }
                          disabled
                          helperText={`Auto-calculated (1 BGN = ${(1/currencySettings.bgn_to_eur_rate).toFixed(4)} EUR)`}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          name="discountPrice"
                          label="Discount Price (BGN) - Optional"
                          value={formik.values.discountPrice || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.discountPrice && Boolean(formik.errors.discountPrice)}
                          helperText={formik.touched.discountPrice && formik.errors.discountPrice}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          name="discountPriceEUR"
                          label="Discount Price (EUR)"
                          value={formik.values.discountPrice ? 
                            convertBgnToEurDynamic(formik.values.discountPrice, currencySettings.bgn_to_eur_rate).toFixed(2) : ''
                          }
                          disabled
                          helperText="Auto-calculated from BGN discount price"
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </>
                  ) : (
                    // EUR Only Mode - Direct EUR input
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          name="price"
                          label="Price (EUR)"
                          value={formik.values.price}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.price && Boolean(formik.errors.price)}
                          helperText={formik.touched.price && formik.errors.price}
                          required
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          name="discountPrice"
                          label="Discount Price (EUR) - Optional"
                          value={formik.values.discountPrice || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.discountPrice && Boolean(formik.errors.discountPrice)}
                          helperText={formik.touched.discountPrice && formik.errors.discountPrice}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      name="stockQuantity"
                      label="Stock Quantity"
                      value={formik.values.stockQuantity}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.stockQuantity && Boolean(formik.errors.stockQuantity)}
                      helperText={formik.touched.stockQuantity && formik.errors.stockQuantity}
                      required
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth error={formik.touched.categoryIds && Boolean(formik.errors.categoryIds)}>
                      <InputLabel>Categories</InputLabel>
                      <Select
                        multiple
                        name="categoryIds"
                        value={formik.values.categoryIds}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Categories"
                        required
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as number[]).map((value) => {
                              const category = categories.find(cat => cat.id === value);
                              return (
                                <Chip key={value} label={category?.name} size="small" />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.categoryIds && formik.errors.categoryIds && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                          {formik.errors.categoryIds}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  
                  {/* Image Upload Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Product Images
                    </Typography>
                    
                    {/* Upload Button for both Create and Edit modes */}
                    <Box mb={2}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                        disabled={uploading}
                      />
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        fullWidth
                        sx={{ p: 2 }}
                      >
                        {uploading ? 'Uploading...' : 'Upload Product Images'}
                      </Button>
                    </Box>
                    
                    {!isEditMode && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Images will be associated with the product after saving.
                      </Alert>
                    )}
                    
                    {/* Image Gallery */}
                    {productImages.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Uploaded Images ({productImages.length})
                        </Typography>
                        <ImageList sx={{ width: '100%', height: 300 }} cols={4} rowHeight={200}>
                          {productImages
                            .sort((a, b) => a.displayOrder - b.displayOrder)
                            .map((image) => (
                            <ImageListItem key={image.id}>
                              <img
                                src={getImageUrl(image.fileUrl)}
                                alt={image.altText || image.fileName}
                                loading="lazy"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain'
                                }}
                              />
                              <ImageListItemBar
                                title={
                                  <Box display="flex" alignItems="center" gap={1}>
                                    {image.isPrimary && (
                                      <Chip
                                        label="Primary"
                                        size="small"
                                        color="primary"
                                        sx={{ height: 20, fontSize: '0.75rem' }}
                                      />
                                    )}
                                    <Typography variant="caption" noWrap>
                                      {image.fileName}
                                    </Typography>
                                  </Box>
                                }
                                actionIcon={
                                  <Box>
                                    <IconButton
                                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                                      onClick={() => handleSetPrimaryImage(image.id)}
                                      disabled={image.isPrimary}
                                      title={image.isPrimary ? 'Primary image' : 'Set as primary'}
                                    >
                                      {image.isPrimary ? <StarIcon /> : <StarBorderIcon />}
                                    </IconButton>
                                    <IconButton
                                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                                      onClick={() => handleDeleteImage(image.id)}
                                      title="Delete image"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                }
                              />
                            </ImageListItem>
                          ))}
                        </ImageList>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Book Details */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Book Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="author"
                      label="Author"
                      value={formik.values.author}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.author && Boolean(formik.errors.author)}
                      helperText={formik.touched.author && formik.errors.author}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="isbn"
                      label="ISBN"
                      value={formik.values.isbn}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.isbn && Boolean(formik.errors.isbn)}
                      helperText={formik.touched.isbn && formik.errors.isbn}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="publisher"
                      label="Publisher"
                      value={formik.values.publisher}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.publisher && Boolean(formik.errors.publisher)}
                      helperText={formik.touched.publisher && formik.errors.publisher}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="date"
                      name="publicationDate"
                      label="Publication Date"
                      value={formik.values.publicationDate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.publicationDate && Boolean(formik.errors.publicationDate)}
                      helperText={formik.touched.publicationDate && formik.errors.publicationDate}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      name="pages"
                      label="Pages"
                      value={formik.values.pages}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.pages && Boolean(formik.errors.pages)}
                      helperText={formik.touched.pages && formik.errors.pages}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="language"
                      label="Language"
                      value={formik.values.language}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.language && Boolean(formik.errors.language)}
                      helperText={formik.touched.language && formik.errors.language}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formik.values.isActive}
                          onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                          name="isActive"
                        />
                      }
                      label="Active Product"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleGoBack}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={loading || !formik.dirty}
              >
                {loading ? 'Saving...' : (isEditMode ? 'Update Product' : 'Create Product')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default ProductEdit;
